import { useState, useEffect, useCallback, createContext, useContext, type ReactNode } from 'react';
import { X, CheckCircle, AlertCircle, Info } from 'lucide-react';

type ToastType = 'success' | 'error' | 'info';

interface Toast {
    id: number;
    message: string;
    type: ToastType;
}

interface ToastContextValue {
    toast: (message: string, type?: ToastType) => void;
}

const ToastContext = createContext<ToastContextValue | null>(null);

export function useToast() {
    const ctx = useContext(ToastContext);
    if (!ctx) throw new Error('useToast must be used within ToastProvider');
    return ctx;
}

let nextId = 0;

export function ToastProvider({ children }: { children: ReactNode }) {
    const [toasts, setToasts] = useState<Toast[]>([]);

    const addToast = useCallback((message: string, type: ToastType = 'info') => {
        const id = ++nextId;
        setToasts(prev => [...prev, { id, message, type }]);
    }, []);

    const removeToast = useCallback((id: number) => {
        setToasts(prev => prev.filter(t => t.id !== id));
    }, []);

    return (
        <ToastContext.Provider value={{ toast: addToast }}>
            {children}
            {/* Toast container. A live region, so the feedback after a write is announced too —
                without it a screen reader hears nothing after "Save" (design audit 2026-10-07,
                S2-11). Errors are not given role="alert" on top: inside a live region they would
                be read twice. */}
            <div className="fixed bottom-4 right-4 z-[60] flex flex-col gap-2 max-w-sm" role="status" aria-live="polite">
                {toasts.map(t => (
                    <ToastItem key={t.id} toast={t} onDismiss={removeToast} />
                ))}
            </div>
        </ToastContext.Provider>
    );
}

function ToastItem({ toast, onDismiss }: { toast: Toast; onDismiss: (id: number) => void }) {
    useEffect(() => {
        const timer = setTimeout(() => onDismiss(toast.id), toast.type === 'error' ? 6000 : 3500);
        return () => clearTimeout(timer);
    }, [toast.id, toast.type, onDismiss]);

    const Icon = toast.type === 'success' ? CheckCircle : toast.type === 'error' ? AlertCircle : Info;
    const colors = toast.type === 'success'
        ? 'bg-green-50 border-green-200 text-green-800'
        : toast.type === 'error'
        ? 'bg-red-50 border-red-200 text-red-800'
        : 'bg-blue-50 border-blue-200 text-blue-800';
    const iconColor = toast.type === 'success' ? 'text-green-500' : toast.type === 'error' ? 'text-red-500' : 'text-blue-500';

    return (
        <div
            className={`flex items-start gap-2 rounded-lg border px-4 py-3 shadow-lg animate-in slide-in-from-right ${colors}`}
        >
            <Icon aria-hidden="true" className={`h-5 w-5 mt-0.5 flex-shrink-0 ${iconColor}`} />
            <p className="text-sm flex-1">{toast.message}</p>
            <button type="button" onClick={() => onDismiss(toast.id)} aria-label="Dismiss" className="flex-shrink-0 opacity-60 hover:opacity-100">
                <X aria-hidden="true" className="h-4 w-4" />
            </button>
        </div>
    );
}
